import React, { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import DESIGN from '../designSystem';

const PassageChart = ({ 
  waypoints = [], 
  onMapClick, 
  onWaypointUpdate, 
  onMapReady,
  mapError,
  setMapError 
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const routeLineRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const loadMap = async () => {
      try {
        if (!window.L) {
          // Load Leaflet dynamically
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => initializeMap();
          document.head.appendChild(script);

          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        } else {
          initializeMap();
        }
      } catch (error) {

        setMapError('Failed to load map library');
      }
    };

    const initializeMap = () => {
      if (!window.L || !mapRef.current) return;

      try {
        const map = window.L.map(mapRef.current).setView([50.8, -1.09], 10);
        
        // Base map layer
        window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        // OpenSeaMap overlay for nautical charts
        window.L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', {
          attribution: '© OpenSeaMap contributors',
          opacity: 0.8
        }).addTo(map);

        // Handle map clicks
        map.on('click', (e) => {
          if (onMapClick) {
            onMapClick(e, map);
          }
        });

        mapInstanceRef.current = map;
        markersRef.current = [];
        
        if (onMapReady) {
          onMapReady(map, markersRef.current, routeLineRef.current);
        }
      } catch (error) {

        setMapError('Failed to initialize map');
      }
    };

    loadMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [onMapClick, onMapReady, setMapError]);

  // Update map when waypoints change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return;

    // Clear existing markers and route
    markersRef.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markersRef.current = [];

    if (routeLineRef.current) {
      mapInstanceRef.current.removeLayer(routeLineRef.current);
      routeLineRef.current = null;
    }

    // Add new markers
    waypoints.forEach((wp, index) => {
      const marker = window.L.marker([wp.lat, wp.lng], {
        draggable: true,
        title: wp.name
      }).addTo(mapInstanceRef.current);

      marker.bindPopup(`
        <div>
          <strong>${wp.name}</strong><br>
          ${wp.lat.toFixed(4)}°, ${wp.lng.toFixed(4)}°
          ${wp.distance ? `<br>Distance: ${wp.distance.toFixed(1)} nm` : ''}
          ${wp.bearing ? `<br>Bearing: ${Math.round(wp.bearing)}°` : ''}
          ${wp.notes ? `<br><em>${wp.notes}</em>` : ''}
        </div>
      `);

      // Handle marker drag
      marker.on('dragend', (e) => {
        const newPos = e.target.getLatLng();
        if (onWaypointUpdate) {
          onWaypointUpdate(wp.id, { lat: newPos.lat, lng: newPos.lng });
        }
      });

      markersRef.current.push(marker);
    });

    // Draw route line
    if (waypoints.length > 1) {
      const latlngs = waypoints.map(wp => [wp.lat, wp.lng]);
      routeLineRef.current = window.L.polyline(latlngs, {
        color: '#4f46e5',
        weight: 3,
        opacity: 0.7
      }).addTo(mapInstanceRef.current);
    }

    // Fit map to show all waypoints
    if (waypoints.length > 0) {
      const bounds = window.L.latLngBounds(waypoints.map(wp => [wp.lat, wp.lng]));
      mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [waypoints, onWaypointUpdate]);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className={DESIGN.header.padding + ' ' + DESIGN.header.borderBottom}>
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 rounded-lg">
            <svg className={DESIGN.icon.medium + ' text-blue-600'} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div>
            <h3 className={DESIGN.header.title}>Electronic Chart</h3>
            <p className={DESIGN.header.subtitle}>
              OpenSeaMap ENC - Click to add waypoints
            </p>
          </div>
        </div>
      </div>
      
      <div className={DESIGN.body.padding}>
        <div 
          ref={mapRef}
          className="w-full h-[400px] rounded-lg border border-gray-200"
          style={{ backgroundColor: '#f7fafc' }}
        >
          {mapError && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600">{mapError}</p>
              </div>
            </div>
          )}
          {!mapInstanceRef.current && !mapError && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Loading nautical chart...</p>
            </div>
          )}
        </div>
        
        {/* Chart disclaimer */}
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">
                Electronic Navigational Chart (ENC) - For Planning Use Only
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                This chart is for passage planning purposes only. Always use official nautical charts and proper navigation equipment for actual navigation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassageChart;